import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parametrosApi, systemApi } from '../../utils/api';
import { Settings, CreditCard, Building2, Calendar, Plus, Pencil, Delete, X, Check, Globe, Building, FileCheck, Percent, DollarSign, RefreshCw, Clock, AlertCircle, CheckCircle, HardDrive } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import AlmacenamientoTab from './AlmacenamientoTab';

const TABS = [
  { id: 'medios-pago', label: 'Medios de Pago', icon: CreditCard },
  { id: 'bancos', label: 'Bancos', icon: Building2 },
  { id: 'dias-credito', label: 'Días de Crédito', icon: Calendar },
  { id: 'impuestos', label: 'Impuestos', icon: Percent },
  { id: 'exoneraciones', label: 'Exoneraciones', icon: FileCheck },
  { id: 'tipo-cambio', label: 'Tipo de Cambio', icon: DollarSign },
  { id: 'almacenamiento', label: 'Almacenamiento', icon: HardDrive },
];

export default function Parametros() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return TABS.find(t => t.id === tabFromUrl) ? tabFromUrl : 'medios-pago';
  });

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };
  const [data, setData] = useState([]);
  const [emisores, setEmisores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadEmisores();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadEmisores = async () => {
    try {
      const res = await parametrosApi.listEmisores();
      setEmisores(res.data.data || []);
    } catch (error) {
      console.error('Error loading emisores:', error);
    }
  };

  const loadData = async () => {
    // tipo-cambio y almacenamiento manejan su propio loading
    if (activeTab === 'tipo-cambio' || activeTab === 'almacenamiento') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let res;
      switch (activeTab) {
        case 'medios-pago':
          res = await parametrosApi.listMediosPago();
          break;
        case 'bancos':
          res = await parametrosApi.listBancos();
          break;
        case 'dias-credito':
          res = await parametrosApi.listDiasCredito();
          break;
        case 'exoneraciones':
          res = await parametrosApi.listTiposExoneracion();
          break;
        case 'impuestos':
          res = await parametrosApi.listTarifasIva();
          break;
      }
      setData(res.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!confirm('¿Está seguro de eliminar este registro?')) return;

    try {
      switch (activeTab) {
        case 'medios-pago':
          await parametrosApi.deleteMedioPago(item.id);
          break;
        case 'bancos':
          await parametrosApi.deleteBanco(item.id);
          break;
        case 'dias-credito':
          await parametrosApi.deleteDiasCredito(item.id);
          break;
        case 'exoneraciones':
          await parametrosApi.deleteTipoExoneracion(item.id);
          break;
        case 'impuestos':
          await parametrosApi.deleteTarifaIva(item.id);
          break;
      }
      toast.success('Registro eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        switch (activeTab) {
          case 'medios-pago':
            await parametrosApi.updateMedioPago(editingItem.id, formData);
            break;
          case 'bancos':
            await parametrosApi.updateBanco(editingItem.id, formData);
            break;
          case 'dias-credito':
            await parametrosApi.updateDiasCredito(editingItem.id, formData);
            break;
          case 'exoneraciones':
            await parametrosApi.updateTipoExoneracion(editingItem.id, formData);
            break;
          case 'impuestos':
            await parametrosApi.updateTarifaIva(editingItem.id, formData);
            break;
        }
        toast.success('Registro actualizado');
      } else {
        switch (activeTab) {
          case 'medios-pago':
            await parametrosApi.createMedioPago(formData);
            break;
          case 'bancos':
            await parametrosApi.createBanco(formData);
            break;
          case 'dias-credito':
            await parametrosApi.createDiasCredito(formData);
            break;
          case 'exoneraciones':
            await parametrosApi.createTipoExoneracion(formData);
            break;
          case 'impuestos':
            await parametrosApi.createTarifaIva(formData);
            break;
        }
        toast.success('Registro creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end">
        {activeTab !== 'tipo-cambio' && activeTab !== 'almacenamiento' && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <Plus size={18} />
            Agregar
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border">
        <div className="border-b">
          <nav className="flex -mb-px">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={clsx(
                    'flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'almacenamiento' ? (
            <AlmacenamientoTab />
          ) : activeTab === 'tipo-cambio' ? (
            <TipoCambioConfig />
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando...</p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay registros. Haga clic en "Agregar" para crear uno.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {activeTab === 'dias-credito' ? (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Días</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Descripción</th>
                    </>
                  ) : activeTab === 'impuestos' ? (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Cód.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Descripción</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">%</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Ley Ref.</th>
                    </>
                  ) : activeTab === 'exoneraciones' ? (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Descripción</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Institución</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Ley</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Art.</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Inc.</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">%</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Nombre</th>
                      {activeTab === 'bancos' && (
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Nombre Corto</th>
                      )}
                    </>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Alcance</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Orden</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {activeTab === 'dias-credito' ? (
                      <>
                        <td className="px-4 py-3 font-medium">{item.dias}</td>
                        <td className="px-4 py-3 text-gray-600">{item.descripcion}</td>
                      </>
                    ) : activeTab === 'impuestos' ? (
                      <>
                        <td className="px-4 py-3">
                          <span className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            item.codigoImpuesto === '01' && 'bg-blue-100 text-blue-700',
                            item.codigoImpuesto === '07' && 'bg-red-100 text-red-700',
                            item.codigoImpuesto === '99' && 'bg-orange-100 text-orange-700',
                            !['01', '07', '99'].includes(item.codigoImpuesto) && 'bg-gray-100 text-gray-700'
                          )}>
                            {item.tipoImpuestoNombre || 'IVA'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">{item.codigo}</td>
                        <td className="px-4 py-3 font-medium">{item.descripcion}</td>
                        <td className="px-4 py-3 text-center font-semibold">{item.porcentaje}%</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.leyReferencia || '-'}</td>
                      </>
                    ) : activeTab === 'exoneraciones' ? (
                      <>
                        <td className="px-4 py-3 font-mono text-sm">{item.codigo}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{item.descripcionCorta || item.descripcion_corta}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          [{item.institucionCodigo || item.institucion_codigo || '01'}] {item.institucionNombre || item.institucion_nombre || 'Min. Hacienda'}
                        </td>
                        <td className="px-4 py-3 text-center text-sm">{item.leyNumero || item.ley_numero || '-'}</td>
                        <td className="px-4 py-3 text-center text-sm">{item.leyArticulo || item.ley_articulo || '-'}</td>
                        <td className="px-4 py-3 text-center text-sm">{item.leyInciso || item.ley_inciso || '-'}</td>
                        <td className="px-4 py-3 text-center text-sm">
                          {(item.fechaEmisionDefecto || item.fecha_emision_defecto)
                            ? new Date(item.fechaEmisionDefecto || item.fecha_emision_defecto).toLocaleDateString('es-CR')
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          {item.porcentajeDefecto || item.porcentaje_defecto || 13}%
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-mono text-sm">{item.codigo}</td>
                        <td className="px-4 py-3 font-medium">{item.nombre}</td>
                        {activeTab === 'bancos' && (
                          <td className="px-4 py-3 text-gray-600">{item.nombre_corto || '-'}</td>
                        )}
                      </>
                    )}
                    <td className="px-4 py-3">
                      {(item.emisor_id || item.emisorId) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                          <Building size={12} />
                          {item.emisor_nombre || 'Empresa'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          <Globe size={12} />
                          Global
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500">{item.orden}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={clsx(
                        'px-2 py-1 rounded text-xs font-medium',
                        (item.is_active || item.isActive) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      )}>
                        {(item.is_active || item.isActive) ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-1.5 hover:bg-blue-50 rounded"
                          title="Editar"
                        >
                          <Pencil className="h-5 w-5 text-blue-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Delete className="h-5 w-5 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <FormModal
          type={activeTab}
          item={editingItem}
          emisores={emisores}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// Form Modal Component
function FormModal({ type, item, emisores, onClose, onSave }) {
  const [formData, setFormData] = useState(() => {
    if (item) {
      return { ...item };
    }
    // Defaults for new
    if (type === 'dias-credito') {
      return { dias: '', descripcion: '', orden: 0, is_active: true, emisor_id: null };
    }
    if (type === 'exoneraciones') {
      return {
        codigo: '',
        descripcion: '',
        descripcionCorta: '',
        institucionCodigo: '01',
        institucionNombre: 'Ministerio de Hacienda',
        numeroDocDefecto: '',
        leyNumero: '',
        leyArticulo: '',
        leyInciso: '',
        fechaEmisionDefecto: '',
        porcentajeDefecto: 13,
        requiereNumeroDoc: true,
        requiereFechas: true,
        orden: 0,
        isActive: true
      };
    }
    if (type === 'impuestos') {
      return {
        codigoImpuesto: '01',
        codigo: '',
        descripcion: '',
        porcentaje: 13,
        leyReferencia: '',
        esTransitoria: false,
        esExenta: false,
        conCredito: true,
        orden: 0,
        isActive: true
      };
    }
    return { codigo: '', nombre: '', nombre_corto: '', descripcion: '', orden: 0, is_active: true, emisor_id: null };
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    const action = item ? 'Editar' : 'Nueva';
    switch (type) {
      case 'medios-pago': return `${item ? 'Editar' : 'Nuevo'} Medio de Pago`;
      case 'bancos': return `${item ? 'Editar' : 'Nuevo'} Banco`;
      case 'dias-credito': return `${item ? 'Editar' : 'Nuevo'} Días de Crédito`;
      case 'exoneraciones': return `${action} Tipo de Exoneración`;
      case 'impuestos': return `${action} Impuesto`;
      default: return 'Formulario';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={clsx(
        "bg-white rounded-xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto",
        (type === 'exoneraciones' || type === 'impuestos') ? 'max-w-2xl' : 'max-w-md'
      )}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">{getTitle()}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {type === 'dias-credito' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Días *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.dias}
                  onChange={(e) => setFormData({ ...formData, dias: parseInt(e.target.value) || 0 })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={formData.descripcion || ''}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder={`${formData.dias || 0} días`}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          ) : type === 'impuestos' ? (
            <>
              {/* Fila 1: Tipo de Impuesto, Código, Porcentaje */}
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Impuesto *</label>
                  <select
                    value={formData.codigoImpuesto || '01'}
                    onChange={(e) => setFormData({ ...formData, codigoImpuesto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="01">01 - IVA (Impuesto al Valor Agregado)</option>
                    <option value="02">02 - Impuesto Selectivo de Consumo</option>
                    <option value="03">03 - Impuesto Único a los Combustibles</option>
                    <option value="04">04 - Impuesto a Bebidas Alcohólicas</option>
                    <option value="05">05 - Impuesto Bebidas Envasadas</option>
                    <option value="06">06 - Impuesto a Productos de Tabaco</option>
                    <option value="07">07 - Impuesto 911 (Emergencias)</option>
                    <option value="08">08 - IVA Bienes Usados</option>
                    <option value="99">99 - Timbre Cruz Roja</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                  <input
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    placeholder="08"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje *</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.porcentaje}
                    onChange={(e) => setFormData({ ...formData, porcentaje: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              {/* Fila 2: Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                <input
                  type="text"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Tarifa general 13%"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              {/* Fila 2: Ley Referencia y checkboxes */}
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ley Referencia</label>
                  <input
                    type="text"
                    value={formData.leyReferencia || formData.ley_referencia || ''}
                    onChange={(e) => setFormData({ ...formData, leyReferencia: e.target.value })}
                    placeholder="RLIVA Art.32 Inc.1"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2 flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.esTransitoria || formData.es_transitoria || false}
                      onChange={(e) => setFormData({ ...formData, esTransitoria: e.target.checked })}
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">Transitoria</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.esExenta || formData.es_exenta || false}
                      onChange={(e) => setFormData({ ...formData, esExenta: e.target.checked })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Exenta</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.conCredito !== false && formData.con_credito !== false}
                      onChange={(e) => setFormData({ ...formData, conCredito: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Con Crédito</span>
                  </label>
                </div>
              </div>
            </>
          ) : type === 'exoneraciones' ? (
            <>
              {/* Fila 1: Código, Descripción, % */}
              <div className="grid grid-cols-6 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                  <input
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    placeholder="08"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción Corta *</label>
                  <input
                    type="text"
                    value={formData.descripcionCorta || formData.descripcion_corta || ''}
                    onChange={(e) => setFormData({ ...formData, descripcionCorta: e.target.value })}
                    placeholder="Zona Franca"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción Completa</label>
                  <input
                    type="text"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Exoneración a Zona Franca"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">% Exoner.</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.porcentajeDefecto || formData.porcentaje_defecto || 13}
                    onChange={(e) => setFormData({ ...formData, porcentajeDefecto: parseFloat(e.target.value) || 13 })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {/* Fila 2: Institución */}
              <div className="grid grid-cols-6 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cód. Inst.</label>
                  <input
                    type="text"
                    value={formData.institucionCodigo || formData.institucion_codigo || '01'}
                    onChange={(e) => setFormData({ ...formData, institucionCodigo: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emitida Por (Institución)</label>
                  <input
                    type="text"
                    value={formData.institucionNombre || formData.institucion_nombre || 'Ministerio de Hacienda'}
                    onChange={(e) => setFormData({ ...formData, institucionNombre: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Emisión</label>
                  <input
                    type="date"
                    value={formData.fechaEmisionDefecto || formData.fecha_emision_defecto || ''}
                    onChange={(e) => setFormData({ ...formData, fechaEmisionDefecto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {/* Fila 3: Ley, Artículo, Inciso, No. Documento */}
              <div className="grid grid-cols-6 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ley</label>
                  <input
                    type="text"
                    value={formData.leyNumero || formData.ley_numero || ''}
                    onChange={(e) => setFormData({ ...formData, leyNumero: e.target.value })}
                    placeholder="9635"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Artículo</label>
                  <input
                    type="text"
                    value={formData.leyArticulo || formData.ley_articulo || ''}
                    onChange={(e) => setFormData({ ...formData, leyArticulo: e.target.value })}
                    placeholder="8"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inciso</label>
                  <input
                    type="text"
                    value={formData.leyInciso || formData.ley_inciso || ''}
                    onChange={(e) => setFormData({ ...formData, leyInciso: e.target.value })}
                    placeholder="23"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. Documento</label>
                  <input
                    type="text"
                    value={formData.numeroDocDefecto || formData.numero_doc_defecto || ''}
                    onChange={(e) => setFormData({ ...formData, numeroDocDefecto: e.target.value })}
                    placeholder="Opcional"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col justify-end gap-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiereNumeroDoc !== false && formData.requiere_numero_doc !== false}
                      onChange={(e) => setFormData({ ...formData, requiereNumeroDoc: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">Req. # doc</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiereFechas !== false && formData.requiere_fechas !== false}
                      onChange={(e) => setFormData({ ...formData, requiereFechas: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-700">Req. fechas</span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                <input
                  type="text"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              {type === 'bancos' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Corto</label>
                  <input
                    type="text"
                    value={formData.nombre_corto || ''}
                    onChange={(e) => setFormData({ ...formData, nombre_corto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              {type === 'medios-pago' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <input
                    type="text"
                    value={formData.descripcion || ''}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
              <input
                type="number"
                min="0"
                value={formData.orden || 0}
                onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={(type === 'exoneraciones' ? (formData.isActive !== false) : formData.is_active) ? 'true' : 'false'}
                onChange={(e) => {
                  const isActive = e.target.value === 'true';
                  if (type === 'exoneraciones') {
                    setFormData({ ...formData, isActive });
                  } else {
                    setFormData({ ...formData, is_active: isActive });
                  }
                }}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>

          {type !== 'exoneraciones' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Alcance</label>
              <select
                value={formData.emisor_id || ''}
                onChange={(e) => setFormData({ ...formData, emisor_id: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Global (todas las empresas)</option>
                {emisores.map((e) => (
                  <option key={e.id} value={e.id}>
                    Solo: {e.nombre}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Global = visible para todas las empresas. Específico = solo para la empresa seleccionada.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Guardar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Tipo de Cambio Configuration Component
function TipoCambioConfig() {
  const [config, setConfig] = useState({
    hora_sync: '06:00',
    retry_interval_min: 30,
    max_retries: 3,
    is_active: true
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await systemApi.getExchangeRateConfig();
      if (response.data.success) {
        if (response.data.config) {
          setConfig(response.data.config);
        }
        setLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await systemApi.updateExchangeRateConfig(config);
      if (response.data.success) {
        toast.success('Configuración guardada');
      } else {
        toast.error(response.data.error || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      // Use tenant-specific sync endpoint
      const response = await systemApi.syncExchangeRate();
      if (response.data.success) {
        toast.success(`T/C actualizado: ₡${response.data.venta}`);
        loadConfig(); // Refresh logs
      } else {
        toast.error(response.data.error || 'Error al sincronizar');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-2 text-gray-500">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Config Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Configuration */}
        <div className="bg-gray-50 rounded-lg p-6 border">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock size={20} />
            Configuración de Sincronización
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Sincronización Automática</label>
              <button
                onClick={() => setConfig({ ...config, is_active: !config.is_active })}
                className={clsx(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  config.is_active ? 'bg-green-500' : 'bg-gray-300'
                )}
              >
                <span className={clsx(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  config.is_active ? 'translate-x-6' : 'translate-x-1'
                )} />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de Sincronización Diaria
              </label>
              <input
                type="time"
                value={config.hora_sync}
                onChange={(e) => setConfig({ ...config, hora_sync: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                El sistema consultará el BCCR a esta hora todos los días
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reintentar cada (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={config.retry_interval_min}
                  onChange={(e) => setConfig({ ...config, retry_interval_min: parseInt(e.target.value) || 30 })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máximo Reintentos
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={config.max_retries}
                  onChange={(e) => setConfig({ ...config, max_retries: parseInt(e.target.value) || 3 })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Check size={18} />
                )}
                Guardar
              </button>
              <button
                onClick={handleSyncNow}
                disabled={syncing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
              >
                {syncing ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <RefreshCw size={18} />
                )}
                Sincronizar Ahora
              </button>
            </div>
          </div>
        </div>

        {/* Right: Recent Logs */}
        <div className="bg-gray-50 rounded-lg p-6 border">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign size={20} />
            Últimas Sincronizaciones
          </h3>

          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
              <p>No hay sincronizaciones registradas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log, idx) => (
                <div
                  key={log.id || idx}
                  className={clsx(
                    'flex items-center justify-between p-3 rounded-lg border',
                    log.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {log.status === 'success' ? (
                      <CheckCircle className="text-green-600" size={20} />
                    ) : (
                      <AlertCircle className="text-red-600" size={20} />
                    )}
                    <div>
                      <div className="text-sm font-medium">
                        {log.status === 'success' ? (
                          <span className="text-green-800">
                            Compra: ₡{parseFloat(log.compra).toFixed(2)} | Venta: ₡{parseFloat(log.venta).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-red-800">{log.error_message || 'Error desconocido'}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(log.fecha_sync || log.created_at).toLocaleString('es-CR')}
                        {log.retry_count > 0 && (
                          <span className="ml-2 text-amber-600">(Reintento #{log.retry_count})</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={loadConfig}
            className="w-full mt-4 text-sm text-blue-600 hover:text-blue-700"
          >
            Actualizar lista
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Tipo de Cambio BCCR:</strong> El sistema consulta automáticamente el tipo de cambio del dólar
          del Banco Central de Costa Rica. Si la consulta falla, reintentará según la configuración.
          Los tipos de cambio se almacenan para consultas históricas.
        </p>
      </div>
    </div>
  );
}
